import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import StorageController from '@/v1/components/controllers/StorageController';
import './CookieNoticeOverlay.scss';

const COOKIE_TEMPLATE_SELECTOR = '.template-cookies';
const COOKIE_BRAND = 'nissan';
const COOKIE_DURATION = 365; // 1 year
const ANIMATION_DURATION = 600;

/**
 * IMPORTANT:
 * This component relies on a Handlebars template
 * being present on the page, which already includes
 * localised content. This is being injected through
 * AEM, so you won't see it locally.
 *
 * For local debugging, you can use the HTML below:
 *
    <script type="text/x-handlebars-template" class="template-cookies" data-brand="nissan_en_GB">
        <div class="c_128 c_128-v2">
            <div class="cookies-container">
                <div class="cookies-container--text">
                    <span class="title">COOKIES</span>
                    <p>We use cookies on our website for identification and analysis purposes.
                    By using this website you consent to the storing and accessing of cookies on your device.
                    Find out more&nbsp;<a href="/legal/cookies">here</a>.</p>
                </div>
                <div class="cookies-container--button">
                    <button type="button" class="disclaimer-close-text">Hide this Message</button>
                </div>
            </div>
        </div>
    </script>
 */

const CookieNoticeOverlay = () => {
    const siteInfo = window?.HELIOS?.adobe?.tagging;
    const language = siteInfo?.languageCode;
    const country = siteInfo?.countryCode;
    const cookieName = `${COOKIE_BRAND}_${language}_${country}`;
    const storageOptions = {};
    const template = document.querySelector(COOKIE_TEMPLATE_SELECTOR);
    const content = template && template.textContent;

    const storageManager = new StorageController({
        defaultLifetime: COOKIE_DURATION,
    });

    const [renderOverlay, setRenderOverlay] = useState(content && content.length > 0);
    const [showOverlay, setShowOverlay] = useState(false);

    const overlayRef = useRef();

    const isLocalStorageAvailable = () => typeof window.Storage !== 'undefined';

    const cookieFound = () => storageManager.loadItem(cookieName, storageOptions);

    const onOverlayScroll = (e) => {
        e.stopPropagation();
    };

    const onAcceptClick = (e) => {
        const overlay = overlayRef.current;

        e.target.removeEventListener('click', onAcceptClick);

        if (overlay) {
            overlay.removeEventListener('wheel', onOverlayScroll);
            overlay.removeEventListener('mousewheel', onOverlayScroll);
            overlay.removeEventListener('DOMMouseScroll', onOverlayScroll);
            overlay.removeEventListener('touchstart', onOverlayScroll);
            overlay.removeEventListener('touchmove', onOverlayScroll);
        }

        if (isLocalStorageAvailable()) {
            storageOptions.mode = 'localStorage';
        } else {
            storageOptions.mode = 'cookie';
        }

        storageManager.saveItem(cookieName, true, storageOptions);

        setShowOverlay(false);
        setTimeout(() => setRenderOverlay(false), ANIMATION_DURATION);
    };

    const init = () => {
        const overlay = overlayRef.current;
        const acceptBtn = overlay && overlay.querySelector('.c_128 button');

        if (overlay) {
            overlay.addEventListener('wheel', onOverlayScroll);
            overlay.addEventListener('mousewheel', onOverlayScroll);
            overlay.addEventListener('DOMMouseScroll', onOverlayScroll);
            overlay.addEventListener('touchstart', onOverlayScroll);
            overlay.addEventListener('touchmove', onOverlayScroll);
        }

        if (acceptBtn) {
            acceptBtn.addEventListener('click', onAcceptClick);
        }

        setShowOverlay(true);
    };

    useEffect(() => {
        if (!cookieFound()) {
            setTimeout(init, ANIMATION_DURATION);
        }
    }, []);

    const overlayClass = classNames('CookieNoticeOverlay', {
        'CookieNoticeOverlay-open': showOverlay,
    });

    return (
        <>
            {renderOverlay && (
                <div className={overlayClass} ref={overlayRef}>
                    {/* eslint-disable-next-line react/no-danger */}
                    <div className="CookieNoticeOverlay__inner">
                        <div className="CookieNoticeOverlay__content transform-normal" dangerouslySetInnerHTML={{ __html: content }} />
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieNoticeOverlay;
